import { z } from "zod";
import { intl } from "LocaleWrapper";
import {
	CalculationMethodType,
	CargoQuantityUnit,
	PayableOver,
	DemurrageTerms, DemurrageCountingType,
	ReeferCheckType, DemurrageCalculationPeriodRegime,
	DemurragePayableOver,
	DemurrageCalculationUnit,
	DemurrageCalculationRoundingScope,
	TimeUnit, PjkTariff,
	GasoilConsumptionPriceSource,
	DemurrageCalculationRoundingRule,
	ReferenceRateDateType,
	TierUnit, DemurrageCalculationRoundingUnit,
	ReferenceAdjustmentType, ReferenceRateBase,
	StaffelUnit, QuantityUnit,
	ReferenceRateUnit,
	CallType,
} from "__generated__/graphql";
import { throwError, ZodSchema } from "@validation/utils/schemaUtils";
import { TierValidationSchema } from "view/calculationmethods/validation/tier.zod";
import {
	CalculationMethodRow,
	getRequiredCalculationMethodFields,
	TierRow,
	validateTiers,
} from "view/agreements/validation/requiredUtils";
import { getKeyFromPath, isTierPath, isTopLevelPath, PathType } from "view/agreements/validation/pathUtils";
import { AgreementTypeEnum } from "__generated__/enums";

export type CalculationMethodFormInput = z.input<typeof CalculationMethodValidationSchema>;
export type CalculationMethodFormOutput = z.output<typeof CalculationMethodValidationSchema>;

export const CalculationMethodValidationSchema = z.object({
	id: z.number().nullish(),
	type: z.enum(CalculationMethodType)
		.nullable()
		.refine((data) => data != null, {
			message: intl.formatMessage({ id: "REQUIRED" }),
		}),
	cargoQuantityUnit: z.enum(CargoQuantityUnit).nullish(),
	weightRanges: z.array(z.object({
		from: ZodSchema.requiredField(z.number().int().min(0).max(10000)),
		to: ZodSchema.requiredField(z.number().int().min(0).max(10000)),
		price: ZodSchema.requiredField(z.number().int().min(0)),
	})).nullish(),
	price: ZodSchema.optionalNumber(0, 999999999),
	payableOver: z.enum(PayableOver).nullish(),
	preAgreed: ZodSchema.optionalNumber(),
	minimumPrice: ZodSchema.optionalNumber(0, 999999999),
	minimumQuantityPayable: ZodSchema.optionalNumber(0, 999999999),
	minimumTimeUnitsPayable: ZodSchema.optionalNumber(0, 999999999),
	percentage: ZodSchema.optionalNumber(-999999999, 999999999),
	applicableAgreementTypes: z.array(z.enum(AgreementTypeEnum)).nullish(),
	applicableAgreements: z.array(z.object({
		id: z.number().nullish(),
		uuid: z.string().nullish(),
		type: z.enum(AgreementTypeEnum).nullable(),
		description: z.string().nullish(),
		calculationMethod: z.object({
			type: z.enum(CalculationMethodType).nullable(),
		}).nullable(),
	})).nullish(),
	demurrageTerms: z.enum(DemurrageTerms).nullish(),
	demurragePayableOver: z.enum(DemurragePayableOver).nullish(),
	timeUnit: z.enum(TimeUnit).nullish(),
	pricePerCargoQuantityUnit: ZodSchema.optionalNumber(0, 999999999),
	laytimeAllowed: ZodSchema.optionalNumber(0, 999999999),
	demurrageCountingType: z.enum(DemurrageCountingType).nullish(),
	startCountingTime: ZodSchema.optionalNumber(),
	workingHoursSinceNomination: ZodSchema.optionalNumber(),
	workingDaysSinceNomination: ZodSchema.optionalNumber(),
	demurrageCalculationUnit: z.enum(DemurrageCalculationUnit).nullish(),
	laytimeRoundingRule: z.enum(DemurrageCalculationRoundingRule).nullish(),
	laytimeRoundingUnit: z.enum(DemurrageCalculationRoundingUnit).nullish(),
	laytimeRoundingScope: z.enum(DemurrageCalculationRoundingScope).nullish(),
	laytimePeriodRegime: z.enum(DemurrageCalculationPeriodRegime).nullish(),
	description: z.string().max(255).nullish(),
	defaultCargoActionTypes: z.array(z.enum(CallType)).nullish(),
	customUnit: z.string().max(255).nullish(),
	summary: z.string().nullish(),
	priceSource: z.enum(GasoilConsumptionPriceSource).nullish(),
	referenceRateDateType: z.enum(ReferenceRateDateType).nullish(),
	quantityUnit: z.enum(QuantityUnit).nullish(),
	reeferCheckType: z.enum(ReeferCheckType).nullish(),
	referenceRateUnit: z.enum(ReferenceRateUnit).nullish(),
	referenceRateBase: z.enum(ReferenceRateBase).nullish(),
	route: z.string().nullish(),
	loadLocation: z.object({
		id: z.number(),
		description: z.string().nullish(),
	}).nullish(),
	dischargeLocation: z.object({
		id: z.number(),
		description: z.string().nullish(),
	}).nullish(),
	pjkTariff: z.enum(PjkTariff).nullish(),
	adjustmentType: z.enum(ReferenceAdjustmentType).nullish(),
	adjustmentAmount: ZodSchema.optionalNumber(-999999999, 999999999),
	staffelUnit: z.enum(StaffelUnit).nullish(),
	staffel: z.object({
		id: z.number(),
		description: z.string().nullish(),
		discriminator: z.string(),
		staffelItems: z.array(z.object({
			id: z.number().optional(),
			discriminator: z.string(),
		})).nullish(),
	}).nullish(),
	staffelItem: z.object({
		id: z.number().optional(),
		discriminator: z.string(),
	}).nullish(),
	tierUnit: z.enum(TierUnit).nullish(),
	waterLevelLocation: z.number().nullish(),
	cdniCostIncluded: z.boolean().nullish(),
	tiers: z.array(TierValidationSchema).nullish(),
}).superRefine((row, ctx) => {
	const requiredFields = getRequiredCalculationMethodFields(
		{
			calculationMethodType: row.type ?? null,
			calculationMethod: row ?? undefined,
		}) ?? [];

	requiredFields.forEach((x) => {
		if (x.path == null) {
			return;
		}

		const path = x.path;
		if (isTopLevelPath(path)) {
			const key = path as keyof CalculationMethodRow;
			const value = row[key];

			const isEmpty = (
				value == null
				|| (typeof value === "string" && value.trim().length === 0)
				|| (Array.isArray(value) && value.length === 0)
			);

			if (isEmpty) {
				throwError([ `${path}` ], ctx);
				return;
			}
		} else if (isTierPath(path)) {
			const key = getKeyFromPath(PathType.Tier, path) as keyof TierRow;
			validateTiers((row.tiers ?? []) as TierRow[], key, ctx);
			return;
		}
	});
});
